import { RiskFactorsIndicators } from "~/pages/space/security/components/RiskFactors/types";
import React from "react";
import { alpha, SxProps, Theme } from "@mui/material";
import InternetExposed from "@mui/icons-material/LanguageOutlined";
import AdminUser from "@mui/icons-material/PersonOutlined";
import AccessToKeys from "@mui/icons-material/KeyOutlined";
import { RiskFactorsTitle } from "~/components/vulnerabilities/risk-factors-title";
import {
  ContainerEnvironmentIcon,
  DatabaseAccessIcon,
  DefensiveIcon,
  EndOfLifeIcon,
  ExploitableIcon,
  HighPriorityIcon,
  LowPriorityIcon,
  PackageIcon,
  PortsIcon,
  RemoteExecutionIcon,
  SensitiveDataIcon,
  VulnerableServiceIcon,
  WarningIcon,
  WifiIcon,
} from "~/icons";

type GetRiskFactorIconParams = {
  indicator: string | null | undefined;
  sx?: SxProps;
};

export const getRiskFactorIcon = ({
  indicator,
  sx: propsSx = {},
}: GetRiskFactorIconParams) => {
  const sx = {
    cursor: "pointer",
    ...propsSx,
  };

  const riskFactorsIconMap: Record<RiskFactorsIndicators, React.ReactElement> =
    {
      "internet-exposed": <InternetExposed {...{ sx }} />,
      "internet-facing": <InternetExposed {...{ sx }} />,
      "db-access": <DatabaseAccessIcon {...{ sx }} />,
      "user-risk": <AdminUser {...{ sx }} />,
      "asset-in-use": <VulnerableServiceIcon {...{ sx }} />,
      "code-execution": <RemoteExecutionIcon {...{ sx }} />,
      "sensitive-data": <SensitiveDataIcon {...{ sx }} />,
      "patch-available": <WifiIcon {...{ sx }} />,
      credentials: <AccessToKeys {...{ sx }} />,
      containers: <ContainerEnvironmentIcon {...{ sx }} />,
      exploitable: <ExploitableIcon {...{ sx }} />,
      eol: <EndOfLifeIcon {...{ sx }} />,
      defensive: <DefensiveIcon {...{ sx }} />,
      package: <PackageIcon {...{ sx }} />,
      port: <PortsIcon {...{ sx }} />,
      "high-priority": <HighPriorityIcon {...{ sx }} />,
      "low-priority": <LowPriorityIcon {...{ sx }} />,
      default: <WarningIcon {...{ sx }} />,
    };

  return (
    riskFactorsIconMap[(indicator || "default") as RiskFactorsIndicators] || (
      <WarningIcon {...{ sx }} />
    )
  );
};

export type GetRiskFactorColorsParams = {
  theme: Theme;
  light: boolean;
  isPositive: boolean;
  isActive?: boolean;
  isAffected?: boolean;
};

export const getRiskFactorColors = ({
  theme,
  light,
  isPositive,
  isActive = true,
  isAffected = true,
}: GetRiskFactorColorsParams) => {
  if (!isActive)
    return {
      textColor: theme.palette.unknown.main,
      fillColor: theme.palette.unknown.main,
    };

  // Positive: Bad (makes score worse), Negative: Good (makes score better)
  const positive = isPositive;
  const warnColorMain = theme.palette.warning.main;
  const warnTextColor = theme.palette.text.warning;
  const warnFillColor = light ? warnColorMain : alpha(warnColorMain, 0.1);
  const goodTextColor = theme.palette.success.main;
  const goodFillColor = alpha(goodTextColor, 0.1);
  const textColor = positive && isAffected ? warnTextColor : goodTextColor;
  const fillColor = positive && isAffected ? warnFillColor : goodFillColor;
  return { textColor, fillColor };
};

export const createRiskFactorSectionTitle = (
  riskFactors: RiskFactorsTitle["riskFactors"],
  { hideValues }: { hideValues?: boolean } = {},
) => {
  return (
    riskFactors
      ?.map((rf) =>
        !hideValues ? `${rf?.affected}/${rf?.total} ${rf?.title}` : rf?.title,
      )
      .join(", ") || ""
  );
};
